// PALETTE FROM POSTER — pure color-reading helpers for the color shelf's two
// "smart" rows (2026-07-21). NO canvas mutation, NO React: given a fabric
// canvas these read the colors already ON the poster; given one color these
// suggest a few that sit well beside it. The shelf renders them as
// "From this poster" and "Goes well with" — educator-plain, no theory words.
//
// Both are PURE so a test (or a server) can call them with a plain object that
// merely quacks like a fabric canvas (has getObjects()/_objects and objects
// with .fill / .styles). The only dependency is house color math.
import { parseColor, rgbToHex, hexToRgb } from '../colorMath.mjs';

// ---------------------------------------------------------------------------
// "From this poster"

const MERGE_DIST = 12; // RGB euclidean radius — merge only near-identical hues
const MAX_SWATCHES = 12;
const STYLE_WALK_BUDGET = 800; // cap the per-char styles scan on huge text

/** Any color string → lowercase '#rrggbb', or null (unparseable / fully
 * transparent — a see-through fill is not a poster color). */
function toHex(c) {
  const p = parseColor(typeof c === 'string' ? c : String(c ?? ''));
  if (!p || p.a === 0) return null;
  return rgbToHex(p).toLowerCase();
}

/** Push every solid color hiding in a fill (string solid, gradient stops of
 * either FillValue `stops` or fabric `colorStops`, or {type:'solid'}). */
function harvestFill(fill, sink) {
  if (!fill) return;
  if (typeof fill === 'string') { const h = toHex(fill); if (h) sink.push(h); return; }
  const stops = fill.stops ?? fill.colorStops;
  if (Array.isArray(stops)) {
    stops.forEach((s) => { const h = toHex(s?.color); if (h) sink.push(h); });
    return;
  }
  if (fill.type === 'solid' && fill.color) { const h = toHex(fill.color); if (h) sink.push(h); }
  // textures carry no extractable color — skip
}

/** Colors from one object: its fill, plus per-character fills for text (styles
 * walk, budgeted so a novel-length textbox can't stall the picker). */
function harvestObject(obj, sink) {
  harvestFill(obj?.fill, sink);
  const styles = obj?.styles;
  if (styles && typeof styles === 'object') {
    let budget = STYLE_WALK_BUDGET;
    for (const line of Object.values(styles)) {
      if (!line || typeof line !== 'object') continue;
      for (const st of Object.values(line)) {
        if (budget <= 0) break;
        budget -= 1;
        if (st && st.fill) harvestFill(st.fill, sink);
      }
      if (budget <= 0) break;
    }
  }
}

/** Flatten a fabric canvas to every object, descending into groups. */
function flattenObjects(canvas) {
  const roots = typeof canvas?.getObjects === 'function'
    ? canvas.getObjects()
    : (Array.isArray(canvas?._objects) ? canvas._objects : []);
  const flat = [];
  const walk = (arr) => (Array.isArray(arr) ? arr : []).forEach((o) => {
    if (!o) return;
    flat.push(o);
    if (typeof o.getObjects === 'function') walk(o.getObjects());
  });
  walk(roots);
  return flat;
}

/** The workspace / clip backdrop — its fill is a real color but a BACKGROUND
 * one, so it is harvested last (lowest priority). Mirrors the editor's
 * `name === 'clip'` convention; a couple of common aliases for safety. */
function isWorkspace(o) {
  return o?.name === 'clip' || o?.name === 'workspace'
    || o?.isWorkspace === true || o?.excludeFromExport === true;
}

/**
 * The colors actually used on the poster, most-used first.
 * @param {object} canvas fabric canvas (or a getObjects()-quacking stand-in)
 * @returns {{hex:string,count:number}[]} up to 12, near-duplicates merged.
 */
export function extractPosterPalette(canvas) {
  if (!canvas) return [];
  const objects = flattenObjects(canvas);
  const foreground = [];
  const background = [];
  objects.forEach((o) => harvestObject(o, isWorkspace(o) ? background : foreground));

  // foreground first so poster art outranks the backdrop on ties
  const ordered = [...foreground, ...background];

  const buckets = []; // { r,g,b, hex, count } — dedupe by perceptual distance
  ordered.forEach((hex) => {
    const c = hexToRgb(hex);
    if (!c) return;
    const near = buckets.find((b) => {
      const dr = b.r - c.r; const dg = b.g - c.g; const db = b.b - c.b;
      return Math.sqrt(dr * dr + dg * dg + db * db) <= MERGE_DIST;
    });
    if (near) near.count += 1;
    else buckets.push({ r: c.r, g: c.g, b: c.b, hex, count: 1 });
  });

  return buckets
    .sort((a, b) => b.count - a.count) // Array.sort is stable → ties keep order
    .slice(0, MAX_SWATCHES)
    .map((b) => ({ hex: b.hex, count: b.count }));
}

// ---------------------------------------------------------------------------
// "Goes well with" — plain HSL wheel math, no theory jargon on screen.

function rgbToHsl({ r, g, b }) {
  const R = r / 255; const G = g / 255; const B = b / 255;
  const max = Math.max(R, G, B); const min = Math.min(R, G, B);
  const l = (max + min) / 2;
  const d = max - min;
  let h = 0; let s = 0;
  if (d > 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    if (max === R) h = 60 * (((G - B) / d) % 6);
    else if (max === G) h = 60 * ((B - R) / d + 2);
    else h = 60 * ((R - G) / d + 4);
  }
  if (h < 0) h += 360;
  return { h, s, l };
}

function hslToHex({ h, s, l }) {
  const H = ((h % 360) + 360) % 360;
  const S = Math.min(1, Math.max(0, s));
  const L = Math.min(1, Math.max(0, l));
  const c = (1 - Math.abs(2 * L - 1)) * S;
  const x = c * (1 - Math.abs(((H / 60) % 2) - 1));
  const m = L - c / 2;
  const [r1, g1, b1] = H < 60 ? [c, x, 0]
    : H < 120 ? [x, c, 0]
      : H < 180 ? [0, c, x]
        : H < 240 ? [0, x, c]
          : H < 300 ? [x, 0, c]
            : [c, 0, x];
  return rgbToHex({
    r: Math.round((r1 + m) * 255),
    g: Math.round((g1 + m) * 255),
    b: Math.round((b1 + m) * 255),
  }).toLowerCase();
}

/**
 * A few colors that sit well beside `hex`, as plain HSL wheel rotations.
 * Grays (no real hue) get a gentle saturation/lightness lift so the
 * suggestions are still visibly colored rather than three identical grays.
 * @param {string} hex any color string
 * @returns {{complement:string[], neighbors:string[], triad:string[]}}
 */
export function harmonyOf(hex) {
  const rgb = hexToRgb(String(hex ?? '')) ?? { r: 17, g: 24, b: 39 };
  const base = rgbToHsl(rgb);
  // vivid, mid-lightness derivations so even a black/white source suggests hue
  const S = base.s < 0.12 ? 0.6 : base.s;
  const L = Math.min(0.72, Math.max(0.32, base.l));
  const at = (deg) => hslToHex({ h: base.h + deg, s: S, l: L });
  return {
    neighbors: [at(-30), at(30)],            // analogous — quiet companions
    complement: [at(180), at(150), at(210)], // opposite + its two split partners
    triad: [at(120), at(240)],               // evenly-spaced trio partners
  };
}

/** Flattened, de-duped "goes well with" chips for a single row (neighbors →
 * complement → triad order), minus the source color itself. Cap ~8. */
export function suggestionsFor(hex) {
  const src = toHex(hex);
  const h = harmonyOf(hex);
  const out = [];
  const seen = new Set(src ? [src] : []);
  [...h.neighbors, ...h.complement, ...h.triad].forEach((c) => {
    const lc = c.toLowerCase();
    if (seen.has(lc)) return;
    seen.add(lc);
    out.push(lc);
  });
  return out.slice(0, 8);
}
